import {
  AuthProvider,
  ensureAuthenticated,
  FeatureFlagProvider,
  getCurrentUserControllerGetCurrentUserQueryOptions,
} from '@artic/api-client';
import { EnvironmentBanner } from '@artic/ui/environment-banner';
import { FullHeightStack } from '@artic/ui/full-height-layout';
import { clearAuthCookies } from '@artic/ui/lib/auth-cookies';
import { PageHeader } from '@artic/ui/page-header';
import { SidebarInset, SidebarProvider } from '@artic/ui/sidebar';
import { Toaster } from '@artic/ui/toast';
import { TooltipProvider } from '@artic/ui/tooltip';
import type { QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useRouterState,
  type AnyRouteMatch,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { NuqsAdapter } from 'nuqs/adapters/tanstack-router';
import { Suspense, type ReactNode } from 'react';

import type { AuthState } from '../auth';
import { getAuthState, redirectToHub } from '../auth';
import { AppLoading } from '../components/app-loading';
import { IntercomIdentity } from '../components/intercom-identity';
import { QualitySidebar } from '../components/quality-sidebar';
import { RouteBreadcrumbs } from '../components/route-breadcrumbs';
import { RouteHeaderActions } from '../components/route-header-actions';
import { SentryIdentity } from '../components/sentry-identity';
import { ThemeProvider } from '../components/theme-provider';
import { usePartnerId } from '../features/inbox/use-partner-name';
import { AccessDeniedToast } from '../features/permissions/access-denied-toast';
import { guardSectionAccess, type GuardLocation } from '../features/permissions/route-guard';
import { RouteReadOnlyBanner } from '../features/permissions/route-read-only-banner';
import { RealtimeProvider } from '../features/realtime/realtime-provider';
import { useCapacityRealtime } from '../features/realtime/use-capacity-realtime';
import { useChangeControlRealtime } from '../features/realtime/use-change-control-realtime';
import { usePartnerRealtimeSync } from '../features/realtime/use-partner-realtime-sync';
import { I18nPartnerSync, I18nProvider } from '../i18n';

export interface RouterContext {
  queryClient: QueryClient;
  auth: AuthState;
  /** The router's last RESOLVED location — undefined on a fresh load, and never
   * overwritten by a denied navigation (it never resolves). Used by the
   * permissions guard to send a denied navigation back where it came from. */
  getPreviousLocation: () => GuardLocation | undefined;
  /** The router's own `matchRoutes`, so the guard can re-check a candidate
   * fallback location without navigating to it first. */
  matchRoutes: (pathname: string) => AnyRouteMatch[];
}

const COOKIE_OPTIONS = (() => {
  const domain = import.meta.env['VITE_AUTH_COOKIE_DOMAIN'] as string | undefined;
  return domain ? { domain } : {};
})();

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({
    context: { queryClient, getPreviousLocation, matchRoutes },
    matches,
    location,
    preload,
  }) => {
    // Read fresh cookie state on every navigation instead of relying on the
    // static snapshot captured at router creation time.
    let auth = getAuthState();

    if (!auth.isAuthenticated) {
      // Neither access token nor refresh token — redirect immediately.
      redirectToHub();
    }

    if (!auth.token) {
      // Access token is missing but refresh token exists — attempt a
      // proactive refresh so the AuthProvider's /current-user call
      // doesn't need the 401 → refresh → retry round-trip.
      const ok = await ensureAuthenticated();
      if (!ok) redirectToHub();

      // Re-read after refresh wrote new cookies.
      auth = getAuthState();
    }

    // Validate the session server-side before rendering. A stored token can
    // outlive its user row (e.g. after `supabase db reset --local`); without
    // this gate the AuthProvider's suspense query throws 404 and the app gets
    // stuck on the root CatchBoundary with no path back to login.
    try {
      await queryClient.ensureQueryData(getCurrentUserControllerGetCurrentUserQueryOptions());
    } catch (error) {
      console.warn('[auth] /current-user rejected, clearing session', error);
      clearAuthCookies(COOKIE_OPTIONS);
      redirectToHub();
    }

    // Permissions v1: block a section the user cannot read. Deliberately AFTER
    // the try/catch above — a redirect thrown inside it would be caught and
    // treated as a failed session, logging the user out.
    const guard = guardSectionAccess({
      queryClient,
      partner: auth.partner,
      matches,
      location,
      preload,
      getPreviousLocation,
      matchRoutes,
    });
    if (!guard.ok && guard.redirectTo) {
      throw redirect({ href: guard.redirectTo, replace: true });
    }

    return { auth };
  },
  component: RootLayout,
});

function useLayoutMode() {
  return useRouterState({
    select: (state) => {
      for (let i = state.matches.length - 1; i >= 0; i--) {
        const layout: unknown = state.matches[i]?.staticData.layout;
        if (typeof layout === 'string') return layout;
      }
      return 'default' as const;
    },
  });
}

function FeatureFlagProviderWithPartner({ children }: { children: ReactNode }) {
  const partnerId = usePartnerId();
  return <FeatureFlagProvider partnerId={partnerId}>{children}</FeatureFlagProvider>;
}

/**
 * App-wide bridge: subscribes the current user to their own partner's realtime
 * topic for partner-scoped events such as feature-flag changes and import
 * status updates. Renders nothing. Must sit inside `RealtimeProvider` (for the
 * socket); `AuthProvider` + `FeatureFlagProvider` are ancestors, so
 * `usePartnerId` resolves.
 */
function PartnerRealtimeSync() {
  const partnerId = usePartnerId();
  usePartnerRealtimeSync(partnerId, { enabled: true });
  return null;
}

/**
 * App-wide bridge to the `change_control:<partnerId>` topic: keeps the change
 * control run progress, Bill of Operations matrix staleness and operations
 * staleness caches live for the whole session. Renders nothing; must sit inside
 * `RealtimeProvider`.
 */
function ChangeControlRealtimeSync() {
  const partnerId = usePartnerId();
  useChangeControlRealtime(partnerId, { enabled: true });
  return null;
}

/**
 * App-wide bridge to the `production_plan:<partnerId>` topic: refetches the
 * ENG-732 capacity matrix (summary cards + expanded view), the planner summary
 * and the planner rows whenever the ENG-721 recompute persists — the
 * split-screen "move a panel, watch the square change" loop. Renders nothing;
 * must sit inside `RealtimeProvider`.
 */
function CapacityRealtimeSync() {
  const partnerId = usePartnerId();
  useCapacityRealtime(partnerId, { enabled: true });
  return null;
}

function RootLayout() {
  const { auth } = Route.useRouteContext();
  const layoutMode = useLayoutMode();

  return (
    <NuqsAdapter>
      <ThemeProvider>
        <TooltipProvider>
          <I18nProvider>
            <AccessDeniedToast />
            <Suspense fallback={<AppLoading />}>
              <AuthProvider activePartner={auth.partner}>
                <I18nPartnerSync />
                <IntercomIdentity />
                <SentryIdentity />
                <FeatureFlagProviderWithPartner>
                  <RealtimeProvider>
                    <PartnerRealtimeSync />
                    <ChangeControlRealtimeSync />
                    <CapacityRealtimeSync />
                    <SidebarProvider>
                      <QualitySidebar />
                      {/*
                       * Full-height routes (Gantt timeline, AG Grid, inbox) scroll
                       * internally, so the inset needs a definite viewport height: the
                       * h-16 PageHeader takes its 64px and FullHeightStack fills the
                       * rest. The sidebar wrapper only supplies `min-h-svh` (a minimum),
                       * so without a fixed height the inset grows with its content and
                       * the page spills past the viewport by the header's height instead
                       * of the inner region scrolling. `overflow-y-auto` (not hidden) so
                       * the odd full-height route that is a tall document (e.g. the
                       * operation editor) still scrolls rather than being clipped; the
                       * internal-scroll routes fit exactly and never show this scrollbar.
                       * Default routes keep ordinary document scroll.
                       */}
                      <SidebarInset
                        className={
                          layoutMode === 'full-height' ? 'h-svh overflow-y-auto' : undefined
                        }
                      >
                        <EnvironmentBanner />
                        <PageHeader actions={<RouteHeaderActions />}>
                          <RouteBreadcrumbs />
                        </PageHeader>
                        {layoutMode === 'full-height' ? (
                          <FullHeightStack>
                            <RouteReadOnlyBanner fullHeight />
                            <Outlet />
                          </FullHeightStack>
                        ) : (
                          <div className="flex flex-1 flex-col gap-4 p-4 pt-0">
                            <RouteReadOnlyBanner />
                            <Outlet />
                          </div>
                        )}
                      </SidebarInset>
                    </SidebarProvider>
                  </RealtimeProvider>
                </FeatureFlagProviderWithPartner>
              </AuthProvider>
            </Suspense>
          </I18nProvider>
          <Toaster position="top-right" richColors closeButton />
          {import.meta.env.DEV && (
            <>
              <ReactQueryDevtools buttonPosition="bottom-left" />
              <TanStackRouterDevtools position="bottom-right" />
            </>
          )}
        </TooltipProvider>
      </ThemeProvider>
    </NuqsAdapter>
  );
}
